import React, { useMemo } from "react";
import { addMonths, formatDateInput, getMonthRange, getYearRange } from "../utils/dateRange";

type PeriodNavigatorProps = {
  fromDate: string;
  toDate: string;
  onChange: (from: string, to: string) => void;
  showToday?: boolean;
  disabled?: boolean;
};

const parseDate = (value: string) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sameRange = (fromA: string, toA: string, fromB: string, toB: string) =>
  fromA === fromB && toA === toB;

export const PeriodNavigator: React.FC<PeriodNavigatorProps> = ({
  fromDate,
  toDate,
  onChange,
  showToday = false,
  disabled = false,
}) => {
  const baseDate = useMemo(() => parseDate(fromDate) ?? new Date(), [fromDate]);
  const currentMonthRange = useMemo(() => {
    const range = getMonthRange(new Date());
    return {
      from: formatDateInput(range.from),
      to: formatDateInput(range.to),
    };
  }, []);
  const currentYearRange = useMemo(() => {
    const range = getYearRange(new Date());
    return {
      from: formatDateInput(range.from),
      to: formatDateInput(range.to),
    };
  }, []);

  const isCurrentMonth = sameRange(fromDate, toDate, currentMonthRange.from, currentMonthRange.to);

  const yearBase = parseDate(fromDate) ?? new Date();
  const yearRange = getYearRange(yearBase);
  const yearRangeFormatted = {
    from: formatDateInput(yearRange.from),
    to: formatDateInput(yearRange.to),
  };
  const isYearRange = sameRange(fromDate, toDate, yearRangeFormatted.from, yearRangeFormatted.to);

  return (
    <div className="btn-group flex-wrap" role="group" aria-label="Periodo">
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        title="Mes anterior"
        disabled={disabled}
        onClick={() => {
          const range = getMonthRange(addMonths(baseDate, -1));
          onChange(formatDateInput(range.from), formatDateInput(range.to));
        }}
      >
        <i className="bi bi-chevron-left me-1" />
        Anterior
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        title="Proximo mes"
        disabled={disabled}
        onClick={() => {
          const range = getMonthRange(addMonths(baseDate, 1));
          onChange(formatDateInput(range.from), formatDateInput(range.to));
        }}
      >
        Proximo
        <i className="bi bi-chevron-right ms-1" />
      </button>
      <button
        type="button"
        className={`btn btn-sm ${isCurrentMonth ? "btn-primary" : "btn-outline-primary"}`}
        title="Mes atual"
        disabled={disabled}
        onClick={() => onChange(currentMonthRange.from, currentMonthRange.to)}
      >
        <i className="bi bi-calendar-event me-1" />
        Mes
      </button>
      <button
        type="button"
        className={`btn btn-sm ${isYearRange ? "btn-primary" : "btn-outline-primary"}`}
        title="Ano todo"
        disabled={disabled}
        onClick={() => onChange(yearRangeFormatted.from, yearRangeFormatted.to)}
      >
        <i className="bi bi-calendar3 me-1" />
        Ano
      </button>
      {showToday && (
        <button type="button" className="btn btn-sm btn-outline-primary" disabled title="Hoje">
          <i className="bi bi-bullseye me-1" />
          Hoje
        </button>
      )}
    </div>
  );
};
